import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions, EventDropArg, EventInput } from '@fullcalendar/core';

import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { EventResizeDoneArg, ThirdPartyDraggable } from '@fullcalendar/interaction';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import { dayJsFullCalendarTimeZonePlugin, dayjs } from '@karve.it/core';
import { Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, createSelector } from '@ngrx/store';

import { capitalize, intersection } from 'lodash';
import { debounceTime, filter, take, withLatestFrom } from 'rxjs';

import { FreyaCommonModule } from 'src/app/freya-common/freya-common.module';
import { isFinalizedInvoice } from 'src/app/invoices/invoices.utils';
import { BookEventDialogActions } from 'src/app/job/job-overview/overview-timeline/timeline-book-event/timeline-book-event.actions';
import { RESOURCE_AREA_HEADER } from 'src/app/schedules/schedule.constants';
import { FreyaHelperService } from 'src/app/services/freya-helper.service';
import { ExtendedCalendarEventForScheduleFragment, FullCalendarHelperService } from 'src/app/services/full-calendar-helper.service';
import { PermissionService } from 'src/app/services/permission.service';
import { ResponsiveHelperService } from 'src/app/services/responsive-helper.service';
import { TimezoneHelperService } from 'src/app/services/timezone-helper.service';

import { environment } from 'src/environments/environment';
import { SubSink } from 'subsink';

import { AvailabilityOutput, BaseAssetFragment, CalendarEventForDispatchFragment, FindAssetFragment } from '../../../generated/graphql.generated';
import { MIN_EVENT_RENDER_TIME, eventTypeInfoMap } from '../../global.constants';
import { EventItineraryActions } from '../../job/job-overview/overview-timeline/timeline-book-event/event-itinerary/event-itinerary.actions';
import { timelineBookEventFeature } from '../../job/job-overview/overview-timeline/timeline-book-event/timeline-book-event.feature';
import { eventScheduleSelectors, selectEventItineraryTotals, selectEventsWithChanges } from '../../job/job-overview/overview-timeline/timeline-book-event/timeline-book-event.selectors';

import { jobToolFeature, selectJobTimezone, selectCustomerName, selectJob } from '../../job/job-tool.reducer';

import { formatCalendarAssetTitle } from '../../shared/assets/assets';
import { SharedModule } from '../../shared/shared.module';
import { LoadingState } from '../../utilities/state.util';


import { BookingScheduleActions } from './booking-schedule.actions';
import { bookingScheduleFeature } from './booking-schedule.feature';
import { selectDepositOutstandingToBookMinor } from './booking-schedule.selectors';
import { ScheduleView , initialScheduleState } from './booking-schedule.state';

const selectScheduleWarnings = createSelector(
    selectCustomerName,
    selectJob,
    eventScheduleSelectors.selectEventMissingLocations,
    selectEventItineraryTotals,
    timelineBookEventFeature.selectBookEventDialog,
    selectDepositOutstandingToBookMinor,
    jobToolFeature.selectAvailabilityLoading,
    (
      customerName,
      job,
      missingLocations,
      { totalTime },
      { showTimeNotAvailableWarning, showAssetsNotAvailableWarning, restrictionsDisabled, windows },
      depositOutstandingMinor,
      availabilityLoading,
    ) => {

      const warnings: Array<{
        severity: 'warn' | 'error';
        type: string;
        text?: string;
        missingLocations?: string[];
        amount?: number;
      }> = [];

      // TODO: implement
      const jobInDifferentTimezone = false;

      if (jobInDifferentTimezone) {
        warnings.push({
          severity: 'warn',
          type: 'jobInDifferentTimezone',
          text: 'You are selecting this events start time in a different timezone.',
        });
      }

      // TODO: implement
      const yemboEstimatorNoEmailWarning = false;

      if (yemboEstimatorNoEmailWarning) {
        warnings.push({
          severity: 'warn',
          type: 'yemboEstimatorNoEmailWarning',
          text: 'This estimator has no Yembo email configured.',
        });
      }

      if (totalTime === 0) {
        warnings.push({
          severity: 'warn',
          type: 'totalTimeIsZero',
          text: 'Total event time is 0. Please adjust the duration of the itinerary accordingly.',
        });
      }

      const hasNoAvailabilityWarning = !availabilityLoading && !restrictionsDisabled && !windows?.length;

      if (customerName === 'No Customer') {
        warnings.push({ severity: 'warn', type: 'noCustomer', text: 'Customer Required' });
      }

      if (job?.zone?.type !== 'area') {
        warnings.push({ severity: 'warn', type: 'zoneNotArea', text: 'Your job needs to be in an area to book events' });
      }

      if (missingLocations?.length && !restrictionsDisabled) {
        warnings.push({
          severity: 'warn',
          type: 'missingLocations',
          text: 'Missing Locations',
          missingLocations,
        });
      }

      if (showTimeNotAvailableWarning && !hasNoAvailabilityWarning) {
        warnings.push({
          severity: 'warn',
          type: 'timeNotAvailable',
          text: 'Start time is not available anymore. Please select another time.',
        });
      }

      if (showAssetsNotAvailableWarning && !hasNoAvailabilityWarning) {
        warnings.push({
          severity: 'warn',
          type: 'assetsNotAvailable',
          text: 'Selected assets are not available for selected time. Please select another time or another asset',
        });
      }

      if (hasNoAvailabilityWarning) {
        warnings.push({
          severity: 'error',
          type: 'noAvailability',
          text: 'No availability on this date',
        });
      }

      if (restrictionsDisabled) {
        warnings.push({
          severity: 'warn',
          type: 'restrictionsDisabled',
          text: 'Restrictions disabled. Double-check to avoid overbooking.',
        });
      }

      if (depositOutstandingMinor) {
        warnings.push({
          severity: 'error',
          type: 'depositRequired',
          amount: depositOutstandingMinor,
          text: 'A deposit is required to book this event.',
        });
      }

      return warnings;
    }
);

export type EventChange = (EventResizeDoneArg | EventDropArg) & {
  notifyAttendees: boolean;
  status: 'Pending' | 'Saving' | 'Saved' | 'Failed';
  hasConflict: boolean;
};

@Component({
  selector: 'app-booking-schedule',
  standalone: true,
  imports: [
    FreyaCommonModule,
    SharedModule,
    FullCalendarModule,
  ],
  templateUrl: './booking-schedule.component.html',
  styleUrls: ['./booking-schedule.component.scss'],
})
export class BookingScheduleComponent implements OnInit, AfterViewInit, OnDestroy {

  // Calendar
  @ViewChild('fc', { static: false }) calendarComponent: FullCalendarComponent;
  // @ViewChild('scheduleOverlay') scheduleOverlay: OverlayPanel;

  @Input() height = '85vh';
  // Change the height based on the number of assets
  @Input() dynamicHeight = true;

  get calendar() {
    return this.calendarComponent?.getApi();
  }

  subs = new SubSink();
  timeOuts: NodeJS.Timeout[] = [];
  events: CalendarEventForDispatchFragment[];
  assets: FindAssetFragment[];

  viewModel$ = this.store.select(createSelector({
    view: createSelector(bookingScheduleFeature.selectBookingSchedule, ({ view }) => view),
    title: createSelector(bookingScheduleFeature.selectBookingSchedule, ({ title }) => title),
    showEventsOnMonthView: createSelector(bookingScheduleFeature.selectBookingSchedule, ({ dayMaxEvents }) => dayMaxEvents === false),
    isMonthView: createSelector(bookingScheduleFeature.selectBookingSchedule, ({ view }) => view === 'dayGridMonth'),
    scheduleLoading: createSelector(
      bookingScheduleFeature.selectBookingSchedule,
      jobToolFeature.selectScheduleEventsLoading,
      jobToolFeature.selectAvailabilityLoading,
      ({ view }, scheduleEventsLoading, availabilityLoading) => {
        if (view === 'dayGridMonth') {
          return scheduleEventsLoading;
        }
        return scheduleEventsLoading || availabilityLoading;
      }
    ),
    monthDate: createSelector(
      bookingScheduleFeature.selectBookingSchedule,
      ({ start }) => dayjs(start).format('MMMM YYYY'),
    ),
    restrictionsDisabled: createSelector(
      timelineBookEventFeature.selectBookEventDialog,
      ({ restrictionsDisabled }) => restrictionsDisabled,
    ),
    loading: createSelector(jobToolFeature.selectCallState, (callState) => {

      const states = [
        callState.ScheduleEvents,
        callState.Assets,
        callState.Availability,
      ];

      return states.some((s) => s === LoadingState.LOADING);
    }),
    // Warning-related properties
    customerName: selectCustomerName,
    job: selectJob,
    warnings: selectScheduleWarnings,
    hasAnyWarning: createSelector(
      selectScheduleWarnings,
      (warnings) => warnings.length > 0,
    ),
    dateRemarks: createSelector(
      bookingScheduleFeature.selectBookingSchedule,
      ({ viewDates }) => (viewDates || [])
        .flatMap((d) => (d?.remarks ?? []).map((r) => ({
          date: d.date,
          text: r.text,
          severity: r.severity || 'info',
        }))),
    ),
  }));

  public BookingScheduleActions = BookingScheduleActions;

  public calendarOptions: CalendarOptions;

  constructor(
    // Angular
    public store: Store,
    private actions: Actions,
    // Helpers
    public responsiveHelper: ResponsiveHelperService,
    private freyaHelper: FreyaHelperService,
    private fcHelper: FullCalendarHelperService,
    private timeZoneHelper: TimezoneHelperService,
    public permissions: PermissionService,
  ) { }

  // Runs before full calendar has loaded
  ngOnInit(): void {
    this.actions.pipe(ofType(BookingScheduleActions.loadEventsSuccess)).subscribe((action) => {
      this.events = action.events;
    });

    // Initialize calendar options with the date from the store
    this.store.select(timelineBookEventFeature.selectBookEventDialog).pipe(
      take(1),
    ).subscribe(({ date }) => {
      this.calendarOptions = {
        headerToolbar: false,
        plugins: [
          resourceTimelinePlugin,
          interactionPlugin,
          dayGridPlugin,
          dayJsFullCalendarTimeZonePlugin
        ],
        timeZone: this.timeZoneHelper.getCurrentTimezone(), // will be overriden in OnChanges with job timezone
        eventMinWidth: 0.001,
        height: 'auto',
        initialView: 'resourceTimelineDay',
        initialDate: date,
        schedulerLicenseKey: environment.fullCalendarKey,
        eventDisplay: 'block', // Without this dayGrid events don't have backgrounds
        resourceAreaHeaderContent: RESOURCE_AREA_HEADER,
        resourceAreaWidth: this.fcHelper.getResourceAreaWidth(),
        stickyFooterScrollbar: true,
        titleFormat: { // will produce something like "Tuesday, September 18, 2018"
          month: 'long',
          year: 'numeric',
          day: 'numeric',
          weekday: 'short'
        },
        eventResizableFromStart: false,
        eventDurationEditable: false,
        // Sets the granularity for moving events
        snapDuration: '00:05:00',
        // Sets the frequency of the lines
        slotDuration: '01:00:00',
        slotLabelInterval: '02:00:00',
        // Sets the start and end times for the day
        slotMinTime: '06:00:00',
        slotMaxTime: '22:00:00',
        navLinks: false,
        dayMaxEvents: initialScheduleState.bookingSchedule.dayMaxEvents,
        views: {
          dayGridMonth: { // name of view
            titleFormat: { month: 'long', year: 'numeric' },
          },
          resourceTimelineWeek: {
            titleFormat: { month: 'long', day: 'numeric', year: 'numeric' }
          }
        },
        resources: [
          {
            id: 'loading',
            title: 'Loading ...',
          }
        ],
        resourceOrder: 'order',
        events: [],
        eventOrder: 'start',
        // Do not automatically scroll the calendar when dragging an event on mobile (makes dragging awkward)
        dragScroll: !this.responsiveHelper.isSmallScreen,
        longPressDelay: this.responsiveHelper.isSmallScreen ? this.responsiveHelper.dragDelay : undefined,
        nowIndicator: true,
        // Requirement: time now indicator moves every minute instead of jumping hour to hour
        nowIndicatorSnap: false,

        moreLinkDidMount: (arg) => {
          // @ts-ignore
          const date = arg.el.closest('.fc-day').dataset.date;
          const eventsForDay = (this.events || [])
            .filter((ev) => dayjs(ev.start * 1000).format('YYYY-MM-DD') === date);

          const content = document.createElement('div');
          content.classList.add('content');

          // TODO:  FIX
          const assetTypes = [];
          const filteredEventsForDay = eventsForDay
            .filter(event => !assetTypes.length || event.assets.some((asset) => assetTypes.includes(asset.type)));
          const totalEl = document.createElement('div');
          if (filteredEventsForDay.length > 0) {
            const formattedDate = dayjs(date).format('YYYY-MM-DD');
            totalEl.classList.add('total');
            totalEl.style.color = 'var(--text-color)';
            totalEl.title = `Discounted Subtotal for ${formattedDate}`;
            const totalAmount = (filteredEventsForDay.reduce((total, current) => total + current.discountedSubTotal, 0) / 100).toFixed(2);
            totalEl.innerText = `$${totalAmount}`;
          }

          const eventBadgesEl = document.createElement('div');
          eventBadgesEl.classList.add('badges-container');
          const eventTypesToDisplay = {};

          for (const event of filteredEventsForDay) {
            if (event.type === 'book-off') {
              continue;
            }
            if (eventTypesToDisplay[event.type]) {
              eventTypesToDisplay[event.type].push(event);
              continue;
            }
            eventTypesToDisplay[event.type] = [event];
          }
          // Create a badge for every event type present on the day
          for (const eventType of Object.keys(eventTypesToDisplay)) {
            const badge = document.createElement('div');
            badge.title = `${eventTypesToDisplay[eventType]?.length} ${capitalize(eventType)} events`;
            badge.innerText = `${eventTypesToDisplay[eventType]?.length}`;

            const backgroundColor = eventTypeInfoMap[eventType]?.backgroundColor || 'primary-color';

            badge.style.backgroundColor = `var(--${backgroundColor})`;
            eventBadgesEl.append(badge);
          }

          content.append(totalEl, eventBadgesEl);

          arg.el.append(content);
        },
        moreLinkClick: (args) => {
          const date = dayjs(args.date).tz(args.view.getOption('timeZone')).format('YYYY-MM-DD');
          this.store.dispatch(this.BookingScheduleActions.monthDateClicked({ date }));
          return 'resourceTimelineDay';
        },
        moreLinkContent: (_args) => {

          const dummyDiv = document.createElement('div');
          dummyDiv.style.display = 'none';

          return { domNodes: [ dummyDiv ] };
        },
        resourceLabelContent: (info) => (
          this.fcHelper.generateResourceLabel(info, this.assets)
        ),
        eventContent: (event) => {

          const type: 'availability' | 'calendar-event' | 'book-off' = event.event.extendedProps.type;

          if (type === 'availability') {
            return {
              domNodes: [ this.fcHelper.createFreyaAvailability(false) ],
            }
          }

          const calendarEvent: ExtendedCalendarEventForScheduleFragment = event.event.extendedProps.event;

          if (type === 'book-off') {
            return {
              domNodes: [ this.fcHelper.createBookedOffEvent(calendarEvent, event.view.type) ],
            }
          }

          let disabled = false;

          if (this.freyaHelper.lockDate > calendarEvent.end) {
            disabled = true;
          }

          if (calendarEvent.invoices?.some(isFinalizedInvoice)) {
            disabled = true;
          }

          const eventEl = this.fcHelper.createFreyaEvent(
            event,
            calendarEvent as ExtendedCalendarEventForScheduleFragment,
            this.calendar,
            false,
            false,
            false,
            disabled);


            return { domNodes: [eventEl] };
        },
        eventClassNames: (event) => {

          const classNames = [];

          const calendarEvent: ExtendedCalendarEventForScheduleFragment = event.event.extendedProps.event;

          if (!calendarEvent) {
            return classNames;
          }

          if (calendarEvent.eventIsPlaceholder) {
            classNames.push('fc-placeholder');
          }

          return classNames;
        },
        datesSet: (args) => {
          this.store.dispatch(this.BookingScheduleActions.datesSet({
            title: args.view.title,
            start: dayjs(args.view.currentStart).tz(args.view.getOption('timeZone')).format('YYYY-MM-DD'),
            end: dayjs(args.view.currentEnd).tz(args.view.getOption('timeZone')).format('YYYY-MM-DD'),
            view: args.view.type as ScheduleView,
          }))
        },
        dateClick: (arg) => {
          if (arg.view.type === 'dayGridMonth') {
            this.store.dispatch(BookingScheduleActions.monthDateClicked({ date: arg.dateStr }));
          }
        },
        navLinkWeekClick: (_date, event) => {
          event.preventDefault();
        },
      };
    });
  }

  // Runs after full calendar has loaded
  ngAfterViewInit(): void {

    // Set date (initial date is handled by initialDate option in calendarOptions)
    this.subs.sink = this.actions.pipe(ofType(
      BookingScheduleActions.previousButtonClicked,
      BookingScheduleActions.nextButtonClicked,
      BookingScheduleActions.todayButtonClicked,
      BookingScheduleActions.monthDateClicked,
      BookingScheduleActions.monthChanged,
      BookEventDialogActions.dateSelected,
    ))
    .subscribe((action) => {
      switch (action.type) {
        case BookingScheduleActions.previousButtonClicked.type:
          this.calendar.prev();
          return;
        case BookingScheduleActions.nextButtonClicked.type:
          this.calendar.next();
          return;
        case BookingScheduleActions.todayButtonClicked.type:
          this.calendar.today();
          return;
        case BookingScheduleActions.monthDateClicked.type:
        case BookingScheduleActions.monthChanged.type:
        case BookEventDialogActions.dateSelected.type:
          this.calendar.gotoDate(action.date);
          return;
      }
    });

    this.subs.sink = this.freyaHelper.containerSizeChanged.subscribe(() => {
      this.renderAfterDelay();
    });

    // Set view
    this.subs.sink = this.actions.pipe(ofType(
      BookingScheduleActions.calendarViewToggled,
      BookingScheduleActions.todayButtonClicked,
      BookingScheduleActions.monthDateClicked,
      BookEventDialogActions.dateSelected,
    ))
    .pipe(concatLatestFrom(() => [
      this.store.select(bookingScheduleFeature.selectBookingSchedule),
    ]))
    .subscribe(([ action, { view: currentView } ]) => {

      let nextView: ScheduleView;

      switch (action.type) {
        case BookingScheduleActions.calendarViewToggled.type:
          nextView = currentView === 'resourceTimelineDay' ? 'dayGridMonth' : 'resourceTimelineDay';
          break;
        default:
          nextView = 'resourceTimelineDay';
          break;
      }

      this.changeView(nextView);
    });

    // Set data
    this.subs.sink = this.actions.pipe(
      ofType(
        BookingScheduleActions.loadAssetsSuccess,
        BookingScheduleActions.loadEventsSuccess,
        BookingScheduleActions.loadAvailabilitySuccess,
        BookEventDialogActions.assetsSelected,
        BookEventDialogActions.dateSelected,
        BookEventDialogActions.timeSelected,
        BookEventDialogActions.findAvailabilitySuccess,
        BookEventDialogActions.bookEventError,
        BookEventDialogActions.bookEventSuccess,
        BookEventDialogActions.rescheduleEventSuccess,
        BookEventDialogActions.rescheduleEventError,
        BookEventDialogActions.rescheduleButtonClicked,
        BookingScheduleActions.toggleRestrictionsButtonClicked,
        EventItineraryActions.includeDockTravelTimeCheckboxToggled,
        EventItineraryActions.estimatedTimeAtLocationInputChanged,
        EventItineraryActions.travelTimeToNextLocationInputChanged,
      ),
      // these may all load at once and cause a bunch of lag
      debounceTime(50),
      withLatestFrom(
        this.store.select(bookingScheduleFeature.selectBookingSchedule),
        this.store.select(selectEventsWithChanges),
      ),
    ).subscribe(([ _action, { availability, assets }, events ]) => {
      this.addEventsAndResources(assets, availability, events);
    });

    // Set time zone
    this.subs.sink = this.store.select(selectJobTimezone).pipe(
      filter(Boolean),
    )
    .subscribe((timezone) => this.calendar.setOption('timeZone', timezone));

    // Set day max events
    this.subs.sink = this.actions.pipe(
      ofType(BookingScheduleActions.showEventsInputSwitched),
      concatLatestFrom(() => this.store.select(bookingScheduleFeature.selectBookingSchedule)),
    )
    .subscribe(([ _action, { dayMaxEvents }]) => {
      this.calendar.setOption('dayMaxEvents', dayMaxEvents);
    });

    const container = document.getElementById('droppable-area');

    new ThirdPartyDraggable(container);
  }

  ngOnDestroy() {
    this.clearOverlays();
    this.timeOuts.forEach((t) => clearTimeout(t));
    this.subs.unsubscribe();
    this.freyaHelper.scheduleEditModeEnabled.next(false);
  }

  addEventsAndResources(
    assets: FindAssetFragment[],
    availability: AvailabilityOutput[],
    events: ExtendedCalendarEventForScheduleFragment[],
  ) {
    this.calendar?.batchRendering(() => {
      this.calendar.removeAllEvents();

      this.addResources(assets);

      this.addAvailabilityEvents(availability);

      this.addCalendarEvents(events, assets);

      const loadingResource = this.calendar.getResourceById('loading');
      loadingResource?.remove();
    });
  }

  /**
   * Changes the view based on the view type, throws a warning in edit mode
   *
   * @param viewType The type of you you want to render
   */
  changeView(viewType: string) {
    this.calendar.changeView(viewType);
    this.setCalendarHeight();
  }

  setCalendarHeight(height?: string) {
    if (this.calendar.view.type === 'dayGridMonth') {
      height = 'auto';
    }

    if (!height) { return; }
    this.calendar.setOption('contentHeight', height);
  }

  clearOverlays() {
    const overlays = document.querySelectorAll('.freya-fc-hover');

    for (let i = 0; i < overlays.length; i++) {
      overlays.item(i).remove();
    }
  }

  // Calendar is drawn not dynamic, as such for events not watched by FC (container size) we must call this.
  renderAfterDelay() {
    this.calendar.setOption('resourceAreaWidth', this.fcHelper.getResourceAreaWidth());
    const timeout = setTimeout(() => { // Timeout ensures that the DOM has been updated before we try to redraw
      this.calendar.render();

      // This logic handles setting the top property for the times to never overlap with the dates
      const sourceElement = document.getElementsByClassName('calendar-header-wrapper').item(0);
      const targetElement = document.getElementsByClassName('fc-scrollgrid').item(0).getElementsByTagName('thead')[0];

      // Get the height of the source element
      const sourceElementHeight = sourceElement.clientHeight;

      // Set the top property of the target element based on the height of the source element
      targetElement.style.top = sourceElementHeight + 'px';
    }, 225);

    this.timeOuts.push(timeout);
  }

  addCalendarEvents(
    events: ExtendedCalendarEventForScheduleFragment[],
    assets: BaseAssetFragment[],
  ) {

    for (const ev of events) {
      const start = ev.start;
      const end = ev.end;

      const isBookOffEvent = ev.type === 'book-off';

      // This may contain the id of deleted assets...
      const allAssetIds = ev.assets?.map((a) => a.id) || [];

      // ...therefore, make sure allAssetIds contains the id of at least one existing asset...
      const hasExistingAssets = ev?.assets?.length
        && intersection(allAssetIds, (assets || []).map((a) => a.id)).length;

      // ...otherwise assign 'none' so the event is assigned to the 'Unassigned' resource
      const assetIds = hasExistingAssets ? allAssetIds : ['none'];

      let endForRendering = end;

      if ((end - start) < MIN_EVENT_RENDER_TIME) {
        endForRendering += MIN_EVENT_RENDER_TIME - (end - start);
      }

      // Create Actual Event
      const event: EventInput = {
        title: ev.title,
        groupId: ev.id,
        start: start * 1000,
        end: endForRendering * 1000,
        id: ev.id,
        extendedProps: {
          type: isBookOffEvent ? 'book-off' : 'calendar-event',
          event: ev,
        },
        resourceIds: assetIds,
        editable: false,
        resourceEditable: false,
      };

      this.calendar.addEvent(event);
    }
  }

  // Turn System Assets into Calendar Resources
  addResources(
    assets: FindAssetFragment[],
  ) {
    this.assets = assets;
    this.calendar.refetchResources();

    let order = 0;

    this.calendar.addResource({ // Add a resource for events that don't have any assets
      id: 'none',
      title: formatCalendarAssetTitle('Unassigned'),
      eventColor: 'red',
      type: 'Unassigned',
      order,
    });
    order++;

    for (const asset of assets) {
      this.calendar.addResource({
        id: asset.id,
        title: formatCalendarAssetTitle(asset.name, asset.type, asset.description),
        type: asset.type,
        eventColor: asset.zones[0]?.color,
        extendedProps: {
          id: asset.id,
          type: asset.type,
          name: asset.name,
        },
        order,
      });

      // increment order
      order++;
    }

    if (this.dynamicHeight) {
      this.setCalendarHeight(`${80 + ((this.calendar.getResources().length) * 52)}px`);
    }
  }

  addAvailabilityEvents(
    availabilities: AvailabilityOutput[] | undefined,
  ) {
    for (const object of availabilities) {

      for (const day of object.dayAvailability) {

        // If the asset has no availability, we need to put a dummy availability to show the inverse of
        if (!day.availability?.length) {
          this.calendar.addEvent({
            title: 'Unavailable',
            groupId: `${object.objectId}-availability`,
            start: `${day.date}T00:00:00`,
            end: `${day.date}T00:00:1`,
            display: 'inverse-background',
            // The Asset that this availability belongs to
            resourceIds: [object.objectId],
            color: '#666666',
            extendedProps: {
              type: 'availability',
            },
          });

          continue;
        }

        for (const avail of day.availability) {
          const start = avail.start * 1000;
          const end = avail.end * 1000;

          this.calendar.addEvent({
            title: 'Unavailable',
            groupId: `${object.objectId}-availability`,
            start,
            end,
            display: 'inverse-background',
            // The Asset that this availability belongs to
            resourceIds: [object.objectId],
            color: '#666666',
            extendedProps: {
              type: 'availability',
            },
          });
        }
      }
    }
  }
}
